import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Parameters } from "@/components/calc/Parameters";
import { Results } from "@/components/calc/Results";
import { Devices } from "@/components/calc/Devices";
import { toast } from "sonner";
import { calculate, DEFAULT_PARAMS } from "@/lib/calc";
import { fetchDevices, saveDevice, deleteDevice } from "@/lib/api";
import { useAppSettings } from "@/context/AppSettings";
import { SlidersHorizontal } from "lucide-react";

export default function Calculator() {
  const { currency } = useAppSettings();
  const [params, setParams] = useState(() => {
    try {
      const saved = localStorage.getItem("laser_calc_params");
      return saved ? { ...DEFAULT_PARAMS, ...JSON.parse(saved) } : DEFAULT_PARAMS;
    } catch {
      return DEFAULT_PARAMS;
    }
  });
  const [saved, setSaved] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    localStorage.setItem("laser_calc_params", JSON.stringify(params));
  }, [params]);

  const loadDevices = async () => {
    try { setSaved(await fetchDevices()); } catch (e) { toast.error("Couldn't load saved devices"); }
  };
  useEffect(() => { loadDevices(); }, []);

  const set = (key, value) => {
    setParams((p) => ({ ...p, [key]: value }));
    setActiveId(null);
  };

  const result = useMemo(() => calculate(params), [params]);

  const onLoad = (device) => {
    // Load this laser's full saved calculation back into the calculator.
    setParams({ ...DEFAULT_PARAMS, ...device.params });
    setActiveId(device.id);
    toast.success(`Loaded ${device.name}`);
  };

  const onSave = async ({ name, brand, params }) => {
    const d = await saveDevice({ name, brand, params });
    await loadDevices();
    setActiveId(d.id);
  };

  const onDelete = async (device) => {
    try {
      await deleteDevice(device.id);
      await loadDevices();
      if (activeId === device.id) setActiveId(null);
      toast.success("Laser removed");
    } catch (e) {
      toast.error("Couldn't remove device");
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 mb-4">
              <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />
              <span className="font-mono-label text-[11px] uppercase tracking-widest text-muted-foreground">
                True cost per pulse
              </span>
            </div>
            <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
              Know the real running cost of your laser.
            </h1>
            <p className="text-muted-foreground mt-4 text-base max-w-lg">
              Flashlamp servicing per shot and electricity by machine hour, kept separate from a
              time-based reserve for catastrophic failures. Save each laser you run, then price full
              treatments (pulses + time + wages + overheads) in the Treatment Builder.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <div className="grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-1 min-w-0">
            <h2 className="font-heading font-semibold text-lg mb-4">Parameters</h2>
            <Parameters params={params} set={set} currency={currency} />
          </div>
          <div className="lg:col-span-2 space-y-6 min-w-0">
            <div>
              <h2 className="font-heading font-semibold text-lg mb-4">Cost Breakdown</h2>
              <Results result={result} currency={currency} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg mb-4">My Lasers & Comparison</h2>
              <Devices
                params={params}
                currency={currency}
                saved={saved}
                activeId={activeId}
                onLoad={onLoad}
                onSave={onSave}
                onDelete={onDelete}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
